import type { Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import {
  array,
  boolean,
  check,
  fallback,
  intersect,
  literal,
  minLength,
  nullish,
  object,
  pipe,
  safeParse,
  string,
  transform,
  union,
} from "valibot";
import {
  dataName,
  errorTranslation,
  label,
  optionalTextValue,
  rememberInput,
  required,
  requireText,
  uniqueItems,
} from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { MAX_CHARACTERS_MD, MAX_CHARACTERS_SM, PREDEFINED_CONDITION_KEYS } from "../../constants";
import { useFormServices } from "../../services/formServices";
import uuid from "../../utils/uuid";
import { castArray, isEmpty, isEqual, isNil, omit } from "lodash-es";
import { createEffect, Match, Show, Switch } from "solid-js";
import { defaultStringFilter } from "../../pages/customer/form/filter";
import { SingleSelectInput } from "../../components/menu/Select";
import { MultiSelectInput } from "../../components/menu/MultiSelect";
import { LookupButtonPreview, LookupOptionsPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import PropertySection from "../PropertySection";
import { Toggle } from "../../components/toggle/Toggle";
import { OptionsPropertyInput } from "../OptionsPropertyInput";
import { keyFromSelector } from "i18next";

const DtoSchema = intersect([
  object({
    label_text: string(),
    data_name: string(),
    options: array(object({ id: nullish(string()), name: nullish(string()), email_label: nullish(string()) })),
    sort_alphabetically: fallback(boolean(), false),
    is_multiple: fallback(boolean(), false),
    expand: fallback(boolean(), false),
    required: boolean(),
    remember_input: boolean(),
  }),
  union([
    object({ is_multiple: literal(false), default_value: nullish(string()) }),
    object({ is_multiple: literal(true), default_value: fallback(array(string()), []) }),
  ]),
]);
const options = () =>
  pipe(
    array(
      object({
        key: string(), // Unchanging identifier for platform
        id: requireText(MAX_CHARACTERS_SM), // Value
        name: requireText(MAX_CHARACTERS_SM), // Label
        email_label: optionalTextValue(MAX_CHARACTERS_MD), // (Value in) PDF
      }),
    ),
    minLength(1, errorTranslation(keyFromSelector(($) => $.FIELD_PROPERTY_OPTIONS_MIN))),
    check(uniqueItems("id"), errorTranslation(keyFromSelector(($) => $.FIELD_PROPERTY_OPTIONS_DUPLICATE_VALUE))),
  );
const DomainSchema = intersect([
  object({
    label_text: label(),
    data_name: dataName(),
    options: options(),
    sort_alphabetically: boolean(),
    is_multiple: boolean(),
    expand: boolean(),
    required: required(),
    remember_input: rememberInput(),
  }),
  union(
    [
      object({
        is_multiple: literal(false),
        default_value: nullish(string()),
      }),
      object({
        is_multiple: literal(true),
        default_value: array(string()),
      }),
    ],
    errorTranslation(keyFromSelector(($) => $.FIELD_PROPERTY_DEFAULT_VALUE_INVALID)),
  ),
]);
const ValueSchema = union([
  pipe(
    string(),
    transform((value) => [value]),
  ),
  array(string()),
]);

const getOptionIdByKey = (options: { id: string; key: string }[], value?: string | null) =>
  options.find((option) => option.key === value)?.id;

const getOptionKeyById = (options: { id?: string | null; key: string }[], value?: string | null) =>
  options.find((option) => option.id === value)?.key;

// Single source of truth for `default_value`: drops keys without an option and matches the shape `is_multiple` demands.
const normalizeDefaultValue = (
  options: { key: string }[],
  value: string | string[] | null | undefined,
  isMultiple: boolean,
) => {
  const keys = options.map((option) => option.key);
  const selected = castArray(value ?? []).filter((key) => keys.includes(key!));

  return isMultiple ? selected : (selected[0] ?? null);
};

export type LookupFieldProperties = InferOutput<typeof DomainSchema>;
export type LookupFieldDtoType = InferOutput<typeof DtoSchema>;
type ValueType = InferOutput<typeof ValueSchema>;

const LookupField: Field<LookupFieldProperties, LookupFieldDtoType, ValueType> = {
  type: "lookup",
  category: "choices",
  widget: "com.moreapps:lookup:1",
  schema: DomainSchema,
  dtoSchema: DtoSchema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    options: [
      { key: uuid(), id: "Option 1", name: "Option 1" },
      { key: uuid(), id: "Option 2", name: "Option 2" },
    ],
    default_value: null,
    sort_alphabetically: false,
    is_multiple: false,
    expand: false,
    required: false,
    remember_input: false,
  }),
  ruleBehaviour: {
    conditions: { variant: "select", keys: PREDEFINED_CONDITION_KEYS },
    actions: { variant: "select" },
  },
  filter: {
    type: "string",
    definition: defaultStringFilter,
  },
  stringValue: (value) => value.toString(),
  renderValue: (value, { fieldConfig }) =>
    value.map((id) => fieldConfig.properties.options.find((option) => option.id === id)?.name ?? id).join(", "),
  icon: "documentSearch",
  titleKey: keyFromSelector(($) => $.LOOKUP_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.LOOKUP_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    options: keyFromSelector(($) => $.FIELD_PROPERTY_OPTIONS_LABEL),
    default_value: keyFromSelector(($) => $.FIELD_PROPERTY_DEFAULT_VALUE),
    sort_alphabetically: keyFromSelector(($) => $.FIELD_PROPERTY_SORT_ALPHABETICALLY),
    is_multiple: keyFromSelector(($) => $.FIELD_PROPERTY_IS_MULTIPLE),
    expand: keyFromSelector(($) => $.FIELD_PROPERTY_EXPAND),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
    remember_input: keyFromSelector(($) => $.FIELD_PROPERTY_REMEMBER_INPUT),
  },
  fromDto: (dto) => {
    const options = dto.options.filter((option) => !isNil(option.id)).map((option) => ({ ...option, key: uuid() }));
    const { output } = safeParse(DomainSchema, {
      ...dto,
      options,
      default_value: normalizeDefaultValue(
        options,
        castArray(dto.default_value ?? []).map((id) => getOptionKeyById(options, id)!),
        dto.is_multiple,
      ),
    });
    return output as LookupFieldProperties;
  },
  toDto: (domain) => {
    const { output } = safeParse(DtoSchema, {
      ...domain,
      options: domain.options.map((option) => ({ ...omit(option, "key") })),
      default_value: domain.is_multiple
        ? domain.default_value.map((x) => getOptionIdByKey(domain.options, x))
        : getOptionIdByKey(domain.options, domain.default_value),
    });
    return output as LookupFieldDtoType;
  },
  PreviewContent: (props) => (
    <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
      <Show
        when={props.properties.expand}
        fallback={
          <LookupButtonPreview
            options={props.properties.options}
            default_value={props.properties.default_value}
            expand={false}
          />
        }
      >
        <div class="flex w-full flex-col gap-y-1">
          <LookupButtonPreview
            options={props.properties.options}
            default_value={props.properties.default_value}
            expand
          />
          <Show when={!isEmpty(props.properties.options)}>
            <LookupOptionsPreview
              default_value={props.properties.default_value}
              is_multiple={props.properties.is_multiple}
              options={props.properties.options}
              sort_alphabetically={props.properties.sort_alphabetically}
            />
          </Show>
        </div>
      </Show>
    </PreviewContentLabel>
  ),
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService, currentScope } = useFormServices();

    // Keeps `default_value` in sync with `options` and `is_multiple`
    createEffect(() => {
      const properties = props.data.properties;
      const normalized = normalizeDefaultValue(properties.options, properties.default_value, properties.is_multiple);

      if (!isEqual(normalized, properties.default_value)) {
        props.setValue("default_value", normalized);
      }
    });

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_OPTIONS)}>
          <OptionsPropertyInput
            options={props.data.properties.options}
            onChange={(updated) => props.setValue("options", updated)}
            variant="text"
            currentScope={currentScope}
            error={props.getError("options")}
            nestedErrors={props.getNestedErrors("options")}
          />

          <Switch>
            <Match when={props.data.properties.is_multiple}>
              <MultiSelectInput
                label={t(props.propertyKeys.default_value)}
                value={castArray(props.data.properties.default_value ?? [])}
                onSelect={(selectedOptions) => props.setValue("default_value", selectedOptions)}
                options={props.data.properties.options.map((option) => ({
                  variant: "text",
                  label: option.name,
                  value: option.key,
                }))}
                errorMessage={props.getError("default_value")}
                sameWidth
              />
            </Match>

            <Match when={!props.data.properties.is_multiple}>
              <SingleSelectInput
                label={t(props.propertyKeys.default_value)}
                value={props.data.properties.default_value}
                onSelect={(selectedOption) => props.setValue("default_value", selectedOption)}
                onClear={() => props.setValue("default_value", null)}
                options={props.data.properties.options.map((option) => ({
                  variant: "text",
                  label: option.name,
                  value: option.key,
                }))}
                errorMessage={props.getError("default_value")}
                sameWidth
              />
            </Match>
          </Switch>
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_DISPLAY)}>
          <Toggle
            block
            label={t(props.propertyKeys.sort_alphabetically)}
            checked={props.data.properties.sort_alphabetically}
            setChecked={(value) => props.setValue("sort_alphabetically", value)}
            description={t(($) => $.FIELD_PROPERTY_SORT_ALPHABETICALLY_DESCRIPTION)}
          />
          <Toggle
            block
            label={t(props.propertyKeys.expand)}
            checked={props.data.properties.expand}
            setChecked={(value) => props.setValue("expand", value)}
            description={t(($) => $.FIELD_PROPERTY_EXPAND_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <Toggle
            block
            label={t(($) => $.REMEMBER_INPUT_SWITCH)}
            checked={props.data.properties.remember_input}
            setChecked={(checked) => props.setValue("remember_input", checked)}
            description={t(($) => $.FIELD_PROPERTY_REMEMBER_INPUT_DESCRIPTION)}
          />
          <Toggle
            block
            label={t(props.propertyKeys.is_multiple)}
            checked={props.data.properties.is_multiple}
            setChecked={(value) => props.setValue("is_multiple", value)}
            description={t(($) => $.FIELD_PROPERTY_IS_MULTIPLE_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(LookupField);
